// src/components/social/ChatWindow.jsx
// Chat prive Fynho Social (amis uniquement) - polling, texte, images, ephemere, suppression, blocage

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTheme } from '../../hooks/useTheme';
import * as socialApi from '../../utils/socialApi';

const ChatWindow = ({ userId, friendId, friendUsername, onClose }) => {
  const { theme } = useTheme();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [pendingImage, setPendingImage] = useState(null); // { dataUrl }
  const [isEphemeral, setIsEphemeral] = useState(false);
  const [viewedEphemeral, setViewedEphemeral] = useState({});
  const [sending, setSending] = useState(false);
  const lastIdRef = useRef(0);
  const pollRef = useRef(null);
  const bottomRef = useRef(null);
  const fileInputRef = useRef(null);

  const loadMessages = useCallback(async () => {
    try {
      const res = await socialApi.getMessages(userId, friendId, 0);
      const list = res.messages || [];
      setMessages(list);
      if (list.length > 0) lastIdRef.current = list[list.length - 1].id;
    } catch (err) {
      // silencieux
    }
  }, [userId, friendId]);

  useEffect(() => {
    loadMessages();
    pollRef.current = setInterval(async () => {
      try {
        const res = await socialApi.getMessages(userId, friendId, 0);
        setMessages(res.messages || []);
      } catch (err) {
        // silencieux
      }
    }, 3000);
    return () => clearInterval(pollRef.current);
  }, [loadMessages, userId, friendId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handlePickImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setPendingImage({ dataUrl: reader.result });
    reader.readAsDataURL(file);
  };

  const handleSend = async () => {
    if (!text.trim() && !pendingImage) return;
    setSending(true);
    try {
      await socialApi.sendMessage(
        userId, friendId,
        text.trim() || null,
        pendingImage ? pendingImage.dataUrl : null,
        isEphemeral && !!pendingImage
      );
      setText('');
      setPendingImage(null);
      setIsEphemeral(false);
      loadMessages();
    } catch (err) {
      alert(err.message || 'Message non envoye.');
    } finally {
      setSending(false);
    }
  };

  const handleOpenEphemeral = async (messageId) => {
    try {
      await socialApi.openEphemeral(messageId);
      setViewedEphemeral((prev) => ({ ...prev, [messageId]: true }));
    } catch (err) {
      // silencieux
    }
  };

  const handleDelete = async (messageId) => {
    setMessages((prev) => prev.filter((m) => m.id !== messageId));
    await socialApi.deleteMessage(messageId, userId);
  };

  const handleBlock = async () => {
    if (!window.confirm(`Bloquer ${friendUsername} ? Il/elle ne pourra plus te contacter.`)) return;
    await socialApi.blockUser(userId, friendId);
    onClose();
  };

  const handleReport = async () => {
    const reason = window.prompt('Raison du signalement (ex: harcelement, contenu adulte, arnaque) :');
    if (!reason) return;
    await socialApi.reportContent(userId, 'user', friendId, reason);
    alert('Signalement envoye a Fynho.');
  };

  const renderMessageContent = (msg) => {
    const isMine = String(msg.sender_id) === String(userId);
    if (msg.image_url) {
      const alreadyOpened = !!msg.opened_at;
      const justViewed = viewedEphemeral[msg.id];
      if (msg.is_ephemeral && !isMine) {
        if (alreadyOpened && !justViewed) {
          return <div style={styles(theme).ephemeralPlaceholder}>🔥 Photo deja vue</div>;
        }
        if (!alreadyOpened) {
          return (
            <div style={styles(theme).ephemeralPlaceholder} onClick={() => handleOpenEphemeral(msg.id)}>
              🔥 Photo ephemere — appuie pour voir
            </div>
          );
        }
      }
      return <img src={msg.image_url} alt="envoi" style={styles(theme).chatImage} />;
    }
    return <div>{msg.content}</div>;
  };

  const s = styles(theme);

  return (
    <div style={s.overlay}>
      <div style={s.header}>
        <button style={s.backBtn} onClick={onClose} title="Retour">←</button>
        <div style={s.headerTitle}>{friendUsername}</div>
        <div style={s.headerActions}>
          <button style={s.headerBtn} onClick={handleReport} title="Signaler">🚩</button>
          <button style={s.headerBtn} onClick={handleBlock} title="Bloquer">⛔</button>
          <button style={s.headerBtn} onClick={onClose} title="Fermer">✕</button>
        </div>
      </div>

      <div style={s.messagesArea}>
        {messages.map((msg) => {
          const isMine = String(msg.sender_id) === String(userId);
          return (
            <div key={msg.id} style={{ ...s.messageRow, justifyContent: isMine ? 'flex-end' : 'flex-start' }}>
              <div style={{ ...s.bubble, ...(isMine ? s.bubbleMine : s.bubbleTheirs) }}>
                {renderMessageContent(msg)}
                {isMine && (
                  <button style={s.deleteBtn} onClick={() => handleDelete(msg.id)} title="Supprimer">🗑️</button>
                )}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {pendingImage && (
        <div style={s.previewRow}>
          <img src={pendingImage.dataUrl} alt="preview" style={s.previewImg} />
          <label style={s.ephemeralLabel}>
            <input type="checkbox" checked={isEphemeral} onChange={(e) => setIsEphemeral(e.target.checked)} />
            Ephemere (disparait apres ouverture)
          </label>
          <button style={s.cancelPreviewBtn} onClick={() => setPendingImage(null)}>✕</button>
        </div>
      )}

      <div style={s.inputRow}>
        <button style={s.attachBtn} onClick={() => fileInputRef.current?.click()}>📷</button>
        <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePickImage} />
        <input
          style={s.textInput}
          type="text"
          placeholder="Ecrire un message..."
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSend(); }}
        />
        <button style={s.sendBtn} onClick={handleSend} disabled={sending}>➤</button>
      </div>
    </div>
  );
};

const styles = (theme) => ({
  overlay: { position: 'fixed', inset: 0, backgroundColor: theme.bg, zIndex: 2000, display: 'flex', flexDirection: 'column' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', padding: '14px 16px', borderBottom: `1px solid ${theme.borderSoft}` },
  backBtn: { background: 'none', border: 'none', fontSize: '20px', color: theme.textPrimary, cursor: 'pointer', padding: '4px 6px', flexShrink: 0 },
  headerTitle: { flex: 1, fontSize: '15px', fontWeight: '700', color: theme.textPrimary, textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  headerActions: { display: 'flex', alignItems: 'center', gap: '18px', flexShrink: 0 },
  headerBtn: { background: 'none', border: 'none', fontSize: '17px', cursor: 'pointer', padding: '4px' },
  messagesArea: { flex: 1, overflowY: 'auto', padding: '14px', display: 'flex', flexDirection: 'column', gap: '8px' },
  messageRow: { display: 'flex' },
  bubble: { position: 'relative', maxWidth: '75%', padding: '9px 12px', borderRadius: '14px', fontSize: '14px' },
  bubbleMine: { backgroundColor: theme.gold, color: '#FFFFFF' },
  bubbleTheirs: { backgroundColor: theme.bgCard, color: theme.textPrimary, border: `1px solid ${theme.border}` },
  deleteBtn: { position: 'absolute', top: '-8px', right: '-8px', width: '20px', height: '20px', borderRadius: '50%', border: 'none', backgroundColor: theme.bgInput, fontSize: '10px', cursor: 'pointer' },
  chatImage: { maxWidth: '180px', borderRadius: '10px', display: 'block' },
  ephemeralPlaceholder: { padding: '10px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', textAlign: 'center' },
  previewRow: { display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 16px', borderTop: `1px solid ${theme.borderSoft}` },
  previewImg: { width: '48px', height: '48px', objectFit: 'cover', borderRadius: '8px' },
  ephemeralLabel: { fontSize: '11px', color: theme.textSecondary, display: 'flex', alignItems: 'center', gap: '4px', flex: 1 },
  cancelPreviewBtn: { background: 'none', border: 'none', fontSize: '14px', cursor: 'pointer', color: theme.textSecondary },
  inputRow: { display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 16px', borderTop: `1px solid ${theme.borderSoft}` },
  attachBtn: { background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer' },
  textInput: { flex: 1, padding: '10px 14px', backgroundColor: theme.bgCard, border: `1px solid ${theme.border}`, borderRadius: '20px', color: theme.textPrimary, fontSize: '14px', outline: 'none' },
  sendBtn: { width: '38px', height: '38px', borderRadius: '50%', backgroundColor: theme.gold, color: '#FFFFFF', border: 'none', fontSize: '15px', cursor: 'pointer' },
});

export default ChatWindow;